import { Text } from '@mantine/core';

import {
  SCREENING_RATING_META,
  summariseScreeningCounts,
  type ScreeningCounts,
  type ScreeningRating,
} from '@/types/screening-outcome';
import { StatusBadge } from '../StatusBadge';

/**
 * The screening hit rating pill: Hit / Potential / Clear, or a dimmed dash
 * while a report has no rated remediation run. The tooltip carries the
 * finding breakdown when one is known, and says so when the rating is
 * provisional (match review still holds records for the subject).
 */
export function RatingBadge({
  rating,
  counts,
  provisional = false,
  size = 'sm',
}: Readonly<{
  rating: ScreeningRating | null | undefined;
  counts?: ScreeningCounts | null;
  provisional?: boolean;
  size?: 'xs' | 'sm' | 'md';
}>) {
  if (!rating) {
    return (
      <Text size="sm" c="dimmed" component="span" aria-label="Not rated yet">
        —
      </Text>
    );
  }
  const meta = SCREENING_RATING_META[rating];
  const summary = counts ? summariseScreeningCounts(counts) : meta.description;
  const tooltip = provisional ? `${summary} · provisional: match review pending` : summary;
  return (
    <StatusBadge
      label={meta.label}
      color={meta.color}
      size={size}
      variant={provisional ? 'outline' : 'light'}
      tooltip={tooltip}
      data-rating={rating}
      data-provisional={provisional || undefined}
    />
  );
}
